/**
 * News slicing shared by every surface — the industry-news scraper (tags docs
 * at ingest), /news top tabs, the Home news rail chips and the backfill script
 * — so they all cut the feed identically. Pure: no Firebase, unit-tested in
 * news-topics.test.ts.
 *
 * Two kinds of slice:
 *  • Sub-category (Tech / Business): a PARTITION of the scraped 'industry'
 *    docs — each scraped story is exactly one of the two.
 *  • Topics (DAW / Open Source, Gordon 2026-10-31: "a DAW category … an Open
 *    Source category as well"): CROSS-CUTTING labels stored as an array on
 *    the doc (`news.topics`), so one story can carry both — an Ardour release
 *    is DAW *and* Open Source and shows under both tabs (and still under
 *    Tech). Docs without a stored `topics` array (BB's own articles, rows
 *    scraped before topics existed) are classified from title + excerpt at
 *    read time with the same rules.
 */

export type NewsSubCategory = 'music-tech' | 'music-industry';

export const NEWS_TOPICS = ['daw', 'open-source'] as const;
export type NewsTopic = (typeof NEWS_TOPICS)[number];

/** Tech-press feed names — derive the sub-category for industry rows scraped
 *  before `subCategory` existed. ONE list for /news and Home (they used to
 *  keep two diverging copies). */
export const TECH_FEED_NAMES = [
  'Sound on Sound', 'MusicTech', 'MusicRadar', 'Attack Magazine', 'Bedroom Producers Blog', 'Pro Tools Expert',
  'Tape Op', 'The Pro Audio Files', 'Synthtopia', 'CDM', 'Gearnews', 'Audio Plugin', 'Synth Anatomy',
  'Bobby Owsinski', 'DJ Tech Tools', 'DJ Mag', 'AI Music Wave', 'Music Production Pro', 'FL Studio',
  'Native Instruments', 'rekkerd',
];

export function newsSubCategory(a: { category?: string; subCategory?: string; source?: string }): NewsSubCategory | null {
  if (a.category !== 'industry') return null;
  if (a.subCategory === 'music-tech' || a.subCategory === 'music-industry') return a.subCategory;
  const src = String(a.source || '');
  return TECH_FEED_NAMES.some(f => src.includes(f)) ? 'music-tech' : 'music-industry';
}

/* ─── Topic rules ───────────────────────────────────────────────────────────
 * DAW = news about digital audio workstations (commercial, free, open-source,
 * web and AI DAWs). Ambiguous English words only count with a disambiguating
 * neighbour: "Reason Studios", "Waveform Pro", "Helio Sequencer". A few names
 * that are also common words are matched case-sensitively (MusE, Audacity,
 * Tenacity, Radium).
 */
const DAW_NAMES_I = [
  'ableton(?: live)?', 'bitwig(?: studio)?', 'logic pro', 'fl studio', 'pro tools', 'cubase', 'nuendo',
  'studio one', 'fender studio', 'reaper', 'reason (?:studios|rack|1[0-9])', 'cakewalk', 'digital performer',
  'tracktion(?: waveform| engine)?', 'waveform (?:free|pro|1[0-9])', 'renoise', 'mixcraft', 'acid pro',
  'samplitude', 'magix sequoia', 'ua luna', 'garageband', '(?:harrison )?mixbus', 'serato studio', 'bandlab',
  'soundtrap', 'audiotool', 'soundation', 'amped studio', 'gridsound', 'opendaw', 'ardour', 'lmms', 'zrythm',
  'qtractor', 'rosegarden', 'stargate daw', 'helio (?:sequencer|workstation)', 'ossia score', 'n-track',
];
const DAW_NAMES_CS = ['MusE', 'Audacity', 'Tenacity', 'Radium'];

const DAW_NAME_I = new RegExp(`\\b(?:${DAW_NAMES_I.join('|')})\\b`, 'i');
const DAW_NAME_CS = new RegExp(`\\b(?:${DAW_NAMES_CS.join('|')})\\b`);
/** A DAW name followed by a version number — "Bitwig Studio 6", "Ardour 9.8",
 *  "FL Studio 2025" — reads as news ABOUT that DAW, not a compatibility list. */
const VERSION = String.raw`\s+v?\d+(?:\.\d+)*\b`;
const DAW_VERSIONED_I = new RegExp(`\\b(?:${DAW_NAMES_I.join('|')})${VERSION}`, 'i');
const DAW_VERSIONED_CS = new RegExp(`\\b(?:${DAW_NAMES_CS.join('|')})${VERSION}`);
const DAW_WORD = /\bdaws?\b|digital audio workstations?/i;
/** In a summary, "your DAW" / "any DAW" is plugin-compatibility talk — only a
 *  DAW described as the subject ("a new free DAW", "browser-based DAW") or
 *  the full phrase counts. */
const DAW_SUBJECT = /digital audio workstations?|\b(?:new|free|open[- ]source|browser[- ]based|web[- ]based|online|ai[- ]powered|mobile|linux|modular|collaborative|cloud)\s+(?:\w+\s+)?daws?\b/i;

/* Open Source = open-source / free-software music tools: explicit licence or
 * code-hosting language, or a known open-source project named in the title.
 * NOTE: "free plugin" alone is NOT open source (freeware ≠ open source); those
 * stories keep the existing "Free" tag instead. */
const OSS_TERMS = /\bopen[- ]sourced?\b|\bFOSS\b|\bFLOSS\b|\b(?:[LA])?GPL(?:-?v?[23](?:\.0)?)?\b|\bMIT[- ]licen[cs]ed?\b|\bMIT licen[cs]e\b|\bapache(?: 2\.0)? licen[cs]e\b|\bBSD[- ]licen[cs]ed?\b|\bsource code\b|\bgithub\b|\bcodeberg\b|\bgitlab\b|\blibre (?:software|audio|music|arts)\b|\bfree software foundation\b/i;
const OSS_PROJECTS_I = [
  'ardour', 'lmms', 'zrythm', 'qtractor', 'rosegarden', 'musescore', 'mixxx', 'surge xt', 'surge synth(?:esizer)?',
  'dexed', 'distrho', 'cardinal (?:modular|rack|plugin)', 'vcv rack', 'opendaw', 'ossia(?: score)?', 'zynaddsubfx',
  'zyn-fusion', 'furnace tracker', 'sonic pi', 'supercollider', 'csound', 'airwindows', 'lsp plugins',
  'calf studio gear', 'x42', 'guitarix', 'bespoke synth', 'odin ?2', 'stargate daw', 'helio sequencer',
  'gridsound', 'pipewire', 'jack audio', 'ladspa', 'lv2', 'clap (?:plugins?|format|standard)', 'tidalcycles',
  'hydrogen drum(?: machine)?', 'vitalium',
];
// Names that are also everyday words or people's names — matched with exact
// casing, and the second group only when a version number follows
// ("Carla 2.5.10", not "Carla Bruni"; "Yoshimi 2.3", not the Flaming Lips album).
const OSS_PROJECTS_CS = ['MusE', 'Audacity', 'Tenacity', 'Radium', 'Pure Data'];
const OSS_AMBIGUOUS_CS = ['Carla', 'Cardinal', 'Hydrogen', 'Giada', 'Yoshimi', 'Strudel'];
const OSS_PROJECT_I = new RegExp(`\\b(?:${OSS_PROJECTS_I.join('|')})\\b`, 'i');
const OSS_PROJECT_CS = new RegExp(`\\b(?:${OSS_PROJECTS_CS.join('|')})\\b`);
const OSS_VERSIONED_I = new RegExp(`\\b(?:${OSS_PROJECTS_I.join('|')})${VERSION}`, 'i');
const OSS_VERSIONED_CS = new RegExp(`\\b(?:${[...OSS_PROJECTS_CS, ...OSS_AMBIGUOUS_CS].join('|')})${VERSION}`);

export interface TopicInput {
  title?: string;
  excerpt?: string;
  /** Topics every item from this source carries (an Ardour release feed is DAW + Open Source). */
  feedTopics?: readonly NewsTopic[];
}

/** "…plugins for any DAW", "…in your DAW" — compatibility talk, not DAW news. */
const DAW_COMPAT = /\b(?:(?:for|in|inside|with|into|across|from|to)\s+)?(?:any|your|all(?: major)?|every|most|major|other|their|my)\s+daws?\b|\bdaw[- ]agnostic\b/gi;

function isDaw(title: string, excerpt: string): boolean {
  if (DAW_NAME_I.test(title) || DAW_NAME_CS.test(title) || DAW_WORD.test(title.replace(DAW_COMPAT, ' '))) return true;
  return DAW_SUBJECT.test(excerpt) || DAW_VERSIONED_I.test(excerpt) || DAW_VERSIONED_CS.test(excerpt);
}

function isOpenSource(title: string, excerpt: string): boolean {
  if (OSS_TERMS.test(title) || OSS_PROJECT_I.test(title) || OSS_PROJECT_CS.test(title) || OSS_VERSIONED_CS.test(title)) return true;
  return OSS_TERMS.test(excerpt) || OSS_VERSIONED_I.test(excerpt) || OSS_VERSIONED_CS.test(excerpt);
}

/** Classify a story. Order follows NEWS_TOPICS; never duplicates. */
export function classifyNewsTopics({ title = '', excerpt = '', feedTopics = [] }: TopicInput): NewsTopic[] {
  const t = String(title);
  const e = String(excerpt);
  const found = new Set<NewsTopic>(feedTopics.filter(isNewsTopic));
  if (isDaw(t, e)) found.add('daw');
  if (isOpenSource(t, e)) found.add('open-source');
  return NEWS_TOPICS.filter(x => found.has(x));
}

export function isNewsTopic(v: unknown): v is NewsTopic {
  return typeof v === 'string' && (NEWS_TOPICS as readonly string[]).includes(v);
}

/** Topics of a stored article: the stored array when the doc has one (the
 *  scraper and backfill keep it current), else classified from its text. */
export function newsTopicsOf(a: { topics?: unknown; title?: string; excerpt?: string }): NewsTopic[] {
  if (Array.isArray(a.topics)) return NEWS_TOPICS.filter(x => (a.topics as unknown[]).includes(x));
  return classifyNewsTopics({ title: a.title, excerpt: a.excerpt });
}

export function newsHasTopic(a: { topics?: unknown; title?: string; excerpt?: string }, topic: NewsTopic): boolean {
  return newsTopicsOf(a).includes(topic);
}

/** Brand tags for DAWs and open-source tools, appended to the scraper's
 *  keyword tagger so the DAW / Open Source tabs get useful sub-filter chips.
 *  Proper nouns — shown as-is in every language, like the existing brand tags. */
export const TOPIC_TAG_RULES: { tag: string; match: RegExp }[] = [
  { tag: 'Bitwig',      match: /\bbitwig\b/i },
  { tag: 'Reaper',      match: /\breaper\b|\bcockos\b/i },
  { tag: 'Cubase',      match: /\bcubase\b|\bnuendo\b/i },
  { tag: 'Studio One',  match: /\bstudio one\b|\bfender studio\b/i },
  { tag: 'Reason',      match: /\breason (?:studios|rack|1[0-9])\b/i },
  { tag: 'Renoise',     match: /\brenoise\b/i },
  { tag: 'Ardour',      match: /\bardour\b|\bmixbus\b/i },
  { tag: 'LMMS',        match: /\blmms\b/i },
  { tag: 'Zrythm',      match: /\bzrythm\b/i },
  { tag: 'Audacity',    match: /\bAudacity\b|\bTenacity\b/ },
  { tag: 'MuseScore',   match: /\bmusescore\b/i },
  { tag: 'Mixxx',       match: /\bmixxx\b/i },
  { tag: 'Surge XT',    match: /\bsurge xt\b|\bsurge synth/i },
  { tag: 'VCV Rack',    match: /\bvcv rack\b|\bcardinal (?:modular|rack|plugin)\b|\bdistrho\b/i },
  { tag: 'BandLab',     match: /\bbandlab\b/i },
  { tag: 'openDAW',     match: /\bopendaw\b/i },
];
